import { FC, PropsWithChildren, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { GallerySlide } from '../gallery-slide';
import { ClickPosition } from '~/utils/click-position';
import { useScopedAtomValue, useSetScopedAtom } from '@lemon8/webapp-shared/component';
import { $CurrentGalleryArticle, $CurrentGallerySlide, $GallerySwiper } from '../../atoms';
import { useAtomValue, useSetAtom } from 'jotai';
import { $SimpleCTAModalShow } from '~/models/portal/simple-cta-modal';
import { useTranslation } from 'react-i18next';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { useActiveToArticle } from '~/utils/active/use-active-to-article';

import './style.scss';

export const GallerySection: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const article = useScopedAtomValue($CurrentGalleryArticle);
  const activeToArticle = useActiveToArticle(article);
  const isMobileUser = useAtomValue($IsMobileUser);
  const setSimpleCTAModal = useSetAtom($SimpleCTAModalShow);
  const setGallerySwiper = useSetScopedAtom($GallerySwiper);
  const currentGallerySlide = useScopedAtomValue($CurrentGallerySlide);

  const { imageList, ocrText = [], imageAutoGenDesp = [] } = article;

  const imageListWithExtraInfo = imageList.map((image, index) => {
    return {
      ...image,
      imgOcrText: ocrText[index] || '',
      imgAutoGenDesc: imageAutoGenDesp[index] || '',
    };
  });

  /**
   * open gallery slide modal only once
   */
  const galleryInit = useRef(false);
  const ctaModalHasOpened = useRef(false);
  useEffect(() => {
    if (!galleryInit.current && !currentGallerySlide) {
      return;
    }
    galleryInit.current = true;

    window.Tea('wap_click', {
      current_position: ClickPosition.main_gallery_slide,
    });

    if (!ctaModalHasOpened.current && isMobileUser) {
      setSimpleCTAModal({
        currentPosition: ClickPosition.main_gallery_slide_modal,
        body: t('seo_nextPic_modal_desc'),
        handleClickConfirm: () => activeToArticle(ClickPosition.main_gallery_slide_modal),
      });
      ctaModalHasOpened.current = true;
    }
  }, [currentGallerySlide]);

  return (
    <SwiperSlide className="gallery-slide-gallery">
      <Swiper onSwiper={setGallerySwiper} className="gallery-gallery-swiper" resistanceRatio={0} nested>
        {imageListWithExtraInfo.map((image, index) => (
          <GallerySlide key={index} image={image} index={index} />
        ))}
      </Swiper>
      {children}
    </SwiperSlide>
  );
};

GallerySection.displayName = SwiperSlide.displayName;
